import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'
import { uiScope } from '@/lib/cssScope'
import { PercentDonut, clampPercent } from '@/components/ui/PercentDonut'
import styles from '@/styles/components/ui/SecurityCard.module.css'

export type SecurityCardSize = 'large' | 'small'

export interface SecurityCardProps extends Omit<ComponentProps<'div'>, 'title'> {
  /** Framework name or “Total Security Health” */
  title: string
  /** 0–100, or null when not assessed (renders n/a — never invent 0%) */
  percent: number | null
  /** Summary row vs compact framework card */
  size?: SecurityCardSize
  /** Arc color (from parent, e.g. getCompliancePaletteColor) */
  color?: string
  /** Shown before status value (large only) */
  statusLabel?: ReactNode
  /** Status text, e.g. “Needs Attention” */
  statusText?: ReactNode
  statusColor?: string
  lastImprovedLabel?: ReactNode
  lastImprovedText?: ReactNode
  hoverDetail?: ReactNode
  chartSize?: number
  innerRadius?: number
  outerRadius?: number
}

/** Compliance donut card: `ui-security-card`, elements `__title` / `__chart` / `__status` / `__info`. */
export function SecurityCard({
  title,
  percent,
  size = 'small',
  color = '#10b981',
  statusLabel,
  statusText,
  statusColor,
  lastImprovedLabel,
  lastImprovedText,
  hoverDetail,
  chartSize,
  innerRadius,
  outerRadius,
  className = '',
  onClick,
  ...rest
}: SecurityCardProps) {
  const tipId = useId()
  const assessed = percent != null && Number.isFinite(Number(percent))
  const p = assessed ? clampPercent(Number(percent)) : 0
  const percentLabel = assessed ? `${p}%` : 'n/a'
  const isLarge = size === 'large'
  // Donut must fit inside `dim`: 2 * outerRadius ≤ dim (Recharts draws to outer edge).
  // Thinner ring + smaller outer vs box avoids clipping on small cards.
  const dim = chartSize ?? (isLarge ? 120 : 92)
  const inner = innerRadius ?? (isLarge ? 44 : 35)
  const outer = outerRadius ?? (isLarge ? 52 : 42)

  const root = [
    uiScope('security-card'),
    'ui-security-card',
    styles.root,
    hoverDetail ? styles.hoverHost : '',
    onClick ? styles.rootClickable : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const headlineLarge = (
    <div className={`${styles.headline} ui-security-card__title`}>
      {title} - {percentLabel}
    </div>
  )

  const smallLeading = (
    <div className={`${styles.smallLeading} ui-security-card__leading`}>
      <div className={`${styles.smallTitle} ui-security-card__title`}>{title}</div>
      <div className={`${styles.smallPercent} ui-security-card__percent`}>{percentLabel}</div>
    </div>
  )

  const chart = (
    <PercentDonut
      className={`${styles.chartWrap} ui-security-card__chart`}
      percent={assessed ? p : 0}
      color={assessed ? color : '#94a3b8'}
      size={dim}
      innerRadius={inner}
      outerRadius={outer}
    />
  )

  const infoBlock =
    isLarge &&
    (statusText != null ||
      lastImprovedText != null ||
      statusLabel != null ||
      lastImprovedLabel != null) ? (
      <div className={`${styles.info} ui-security-card__info`}>
        {(statusLabel != null || statusText != null) && (
          <div className={`${styles.statusRow} ui-security-card__status`}>
            {statusLabel != null && <span>{statusLabel}:</span>}
            {statusText != null && (
              <span
                className={styles.statusValue}
                style={statusColor ? { color: statusColor } : undefined}
              >
                {statusText}
              </span>
            )}
          </div>
        )}
        {(lastImprovedLabel != null || lastImprovedText != null) && (
          <div>
            {lastImprovedLabel != null && <span>{lastImprovedLabel} </span>}
            {lastImprovedText != null && <span>{lastImprovedText}</span>}
          </div>
        )}
      </div>
    ) : null

  return (
    <div
      className={root}
      tabIndex={hoverDetail || onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick(e as unknown as React.MouseEvent<HTMLDivElement>)
              }
            }
          : undefined
      }
      aria-describedby={hoverDetail ? tipId : undefined}
      {...rest}
    >
      {isLarge ? (
        <>
          {headlineLarge}
          <div className={styles.largeBody}>
            {chart}
            {infoBlock}
          </div>
        </>
      ) : (
        <div className={styles.smallBody}>
          {smallLeading}
          {chart}
        </div>
      )}
      {hoverDetail ? (
        <div id={tipId} className={`${styles.tooltip} ui-security-card__tooltip`} role="tooltip">
          {hoverDetail}
        </div>
      ) : null}
    </div>
  )
}

export default SecurityCard
